import { useCallback, useEffect, useState } from "react";
import { posCatalogApi, type PosCatalogStatus, type PosCatalogSyncResult } from "../../api/posCatalogApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessPosMonitor } from "../../auth/roles";

export default function PosCatalogPage() {
  const { token, user } = useAuth();
  const canView = canAccessPosMonitor(user?.role);
  const [status, setStatus] = useState<PosCatalogStatus | null>(null);
  const [preview, setPreview] = useState<PosCatalogSyncResult | null>(null);
  const [importResult, setImportResult] = useState<PosCatalogSyncResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [deactivateMissing, setDeactivateMissing] = useState(false);

  const load = useCallback(async () => {
    if (!token || !canView) return;
    setLoading(true);
    setError("");
    try {
      const res = await posCatalogApi.getStatus(token);
      setStatus(res);
      setDeactivateMissing(Boolean(res.deactivateMissingEnabled));
    } catch (e: any) {
      setError(e?.message || "Errore caricamento stato catalogo");
    } finally {
      setLoading(false);
    }
  }, [token, canView]);

  useEffect(() => {
    if (!canView) {
      setError("Accesso riservato a Manager o Admin.");
      return;
    }
    void load();
  }, [load, canView]);

  async function handleTestMidware() {
    if (!token) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await posCatalogApi.testMidware(token);
      setMessage(
        `Midware OK — sessione ${res.sessionCodePreview}, ${res.departmentsReceived} reparti, ${res.productsReceived} prodotti`,
      );
    } catch (e: any) {
      setError(e?.message || "Test Midware fallito");
    } finally {
      setBusy(false);
    }
  }

  async function handlePreview() {
    if (!token) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await posCatalogApi.sync(token, { dryRun: true });
      setPreview(res);
      setMessage(
        res.source === "midware"
          ? `Anteprima Midware: ${res.productsPrepared ?? 0} prodotti, ${res.categoriesPrepared ?? 0} reparti`
          : `Anteprima locale: ${res.productsCount ?? res.productsPrepared ?? 0} prodotti in Corevian`,
      );
    } catch (e: any) {
      setError(e?.message || "Anteprima catalogo fallita");
    } finally {
      setBusy(false);
    }
  }

  async function handleImport() {
    if (!token) return;
    if (!status?.midwareConfigured) {
      setError("Midware non configurato su narcil. Vedi docs/catalog-midware-setup.md");
      return;
    }
    const ok = window.confirm(
      deactivateMissing
        ? "Importare il catalogo da Midware e disattivare i prodotti non più presenti?"
        : "Importare il catalogo completo da Midware in Corevian (PosProduct)?",
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const res = await posCatalogApi.sync(token, { dryRun: false, deactivateMissing });
      setImportResult(res);
      setMessage(
        `Import completato: ${res.productsSynced ?? 0} prodotti, ${res.categoriesSynced ?? 0} reparti` +
          (res.productsDeactivated ? `, ${res.productsDeactivated} disattivati` : ""),
      );
      await load();
    } catch (e: any) {
      setError(e?.message || "Import catalogo fallito");
    } finally {
      setBusy(false);
    }
  }

  if (!canView) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Accesso riservato a Manager o Admin.</div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h4 className="mb-1">Catalogo POS</h4>
          <div className="text-muted small">
            Import reparti e prodotti verso Corevian — da Midware (server) o da Easy Retail Firebird (NATPOS cassa)
          </div>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void load()} disabled={loading || busy}>
          Aggiorna
        </button>
      </div>

      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {message ? <div className="alert alert-success py-2">{message}</div> : null}

      <div className="alert alert-info py-2">
        <strong>Import da Easy Retail (ZANARDELLI).</strong> Midware non è raggiungibile da narcil: usa NATPOS Electron
        sul PC cassa (<code>Strumenti amministrazione</code> → <em>Anteprima Easy Retail</em> /{" "}
        <em>Importa catalogo Easy Retail</em>). Legge <code>C:\EasyRetailPOS\DBase\DBRETAIL.gdb</code> e invia i dati
        a Corevian. Vedi <code>docs/easy-retail-catalog-setup.md</code>.
      </div>

      <div className="row g-3 mb-3">
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">Prodotti attivi</div>
              <div className="fs-3 fw-semibold">{status?.productsCount ?? "—"}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">Reparti attivi</div>
              <div className="fs-3 fw-semibold">{status?.categoriesCount ?? "—"}</div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted small">Fonte catalogo (server)</div>
              <div className="fw-semibold">
                {status?.catalogImportMode === "easy-retail" ? (
                  <span className="text-primary">Easy Retail via NATPOS cassa</span>
                ) : (
                  <span className="text-success">Midware (rete negozio)</span>
                )}
              </div>
              <div className="small text-muted mt-1">
                {status?.catalogImportMode === "easy-retail"
                  ? "Import da DBRETAIL.gdb sul PC cassa → API Corevian"
                  : status?.midwareBaseUrl || "MIDWARE_BASE_URL assente"}
              </div>
              {status?.lastEasyRetailSync ? (
                <div className="small mt-2">
                  Ultimo import ER: {new Date(status.lastEasyRetailSync.createdAt).toLocaleString()} —{" "}
                  {status.lastEasyRetailSync.status}
                  {status.lastEasyRetailSync.message ? ` (${status.lastEasyRetailSync.message})` : ""}
                </div>
              ) : status?.lastMidwareSync ? (
                <div className="small mt-2">
                  Ultimo import Midware: {new Date(status.lastMidwareSync.createdAt).toLocaleString()} —{" "}
                  {status.lastMidwareSync.status}
                  {status.lastMidwareSync.message ? ` (${status.lastMidwareSync.message})` : ""}
                </div>
              ) : (
                <div className="small mt-2 text-muted">Nessun import registrato</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-2 align-items-center">
          <button type="button" className="btn btn-outline-primary" onClick={() => void handleTestMidware()} disabled={busy}>
            Test login Midware
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => void handlePreview()} disabled={busy}>
            Anteprima import
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void handleImport()} disabled={busy || !status?.midwareConfigured}>
            Importa catalogo
          </button>
          <label className="ms-2 form-check form-check-inline mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              checked={deactivateMissing}
              onChange={(e) => setDeactivateMissing(e.target.checked)}
            />
            <span className="form-check-label">Disattiva prodotti assenti in Midware</span>
          </label>
        </div>
      </div>

      {preview ? (
        <div className="card mb-3">
          <div className="card-header">Anteprima</div>
          <div className="card-body">
            <pre className="small mb-0" style={{ maxHeight: 280, overflow: "auto" }}>
              {JSON.stringify(preview, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}

      {importResult ? (
        <div className="card mb-3">
          <div className="card-header">Ultimo import</div>
          <div className="card-body">
            <pre className="small mb-0" style={{ maxHeight: 220, overflow: "auto" }}>
              {JSON.stringify(importResult, null, 2)}
            </pre>
          </div>
        </div>
      ) : null}

      <div className="alert alert-info small mb-0">
        Dopo l&apos;import, sulla cassa NATPOS usa <strong>Aggiorna catalogo</strong> o riapri la sessione. In fase parallela
        (`POS_READ_ONLY=1`) l&apos;import catalogo è consentito anche senza spegnere Easy Retail.
      </div>
    </div>
  );
}
